// Minimal chat theme: hairline borders, flat surfaces, one accent colour.
const LINE = "color-mix(in srgb, var(--ink) 12%, transparent)";
const SOFT = "color-mix(in srgb, var(--ink) 5%, var(--bg-surface))";
const ACCENT_SOFT = "color-mix(in srgb, var(--accent-color) 12%, var(--bg-surface))";

const panel = {
  background: "var(--bg-surface)",
  border: `1px solid ${LINE}`,
  borderRadius: 18,
  boxSizing: "border-box",
};

const styles = {
  layout: {
    display: "flex",
    gap: 16,
    height: "calc(100vh - 220px)",
    minHeight: 420,
  },

  /* --- sidebar --- */
  sidebar: {
    ...panel,
    width: 290,
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    padding: "14px 10px",
    overflowY: "auto",
  },
  searchWrap: {
    position: "relative",
    margin: "0 4px 6px",
    flexShrink: 0,
  },
  searchIcon: {
    position: "absolute",
    left: 12,
    top: "50%",
    transform: "translateY(-50%)",
    color: "var(--ink-faint)",
    display: "flex",
    pointerEvents: "none",
  },
  searchInput: {
    width: "100%",
    boxSizing: "border-box",
    fontFamily: "inherit",
    fontSize: 14,
    color: "var(--ink)",
    background: SOFT,
    border: "1px solid transparent",
    borderRadius: 12,
    padding: "9px 12px 9px 36px",
    outline: "none",
  },
  sidebarGroupTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontSize: 12,
    fontWeight: 600,
    color: "var(--ink-faint)",
    letterSpacing: 0.6,
    margin: "14px 8px 6px",
  },
  sidebarCount: {
    fontWeight: 500,
    fontSize: 11.5,
  },
  sidebarEmpty: {
    fontSize: 13,
    color: "var(--ink-faint)",
    margin: "2px 8px 8px",
  },
  sidebarList: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  sidebarItem: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    width: "100%",
    boxSizing: "border-box",
    padding: "8px 10px",
    borderRadius: 12,
    border: "none",
    background: "transparent",
    cursor: "pointer",
    textAlign: "left",
    fontFamily: "inherit",
    fontSize: 14.5,
    color: "var(--ink)",
    transition: "background 120ms ease",
  },
  sidebarItemActive: {
    background: ACCENT_SOFT,
  },
  sidebarAvatar: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "color-mix(in srgb, var(--accent-color) 22%, var(--bg-surface))",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 600,
    fontSize: 14,
    color: "var(--accent-color)",
    flexShrink: 0,
  },
  sidebarAvatarImg: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    objectFit: "cover",
    flexShrink: 0,
    display: "block",
  },
  sidebarAvatarWrap: {
    position: "relative",
    flexShrink: 0,
    display: "inline-flex",
  },
  sidebarItemName: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  unreadBadge: {
    flexShrink: 0,
    minWidth: 20,
    height: 20,
    padding: "0 6px",
    boxSizing: "border-box",
    borderRadius: 10,
    background: "var(--accent-color)",
    color: "#FFFDF8",
    fontSize: 11,
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  onlineDot: {
    position: "absolute",
    bottom: 0,
    right: 0,
    width: 10,
    height: 10,
    borderRadius: "50%",
    background: "#4CAF50",
    border: "2px solid var(--bg-surface)",
  },
  onlineDotOffline: {
    position: "static",
    border: "none",
    background: "var(--ink-faint)",
    marginRight: 6,
  },
  headerOnlineDot: {
    display: "inline-block",
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "#4CAF50",
    marginRight: 6,
    flexShrink: 0,
  },
  headerOnlineDotOffline: {
    background: "var(--ink-faint)",
  },

  /* --- chat window --- */
  window: {
    ...panel,
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    padding: 0,
    overflow: "hidden",
  },
  windowHeader: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 20px",
    borderBottom: `1px solid ${LINE}`,
    flexShrink: 0,
  },
  windowHeaderAvatar: {
    width: 38,
    height: 38,
    borderRadius: "50%",
    objectFit: "cover",
    flexShrink: 0,
  },
  windowHeaderAvatarFallback: {
    width: 38,
    height: 38,
    borderRadius: "50%",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 600,
    fontSize: 15,
    background: "color-mix(in srgb, var(--accent-color) 22%, var(--bg-surface))",
    color: "var(--accent-color)",
  },
  windowHeaderTitleRow: {
    display: "flex",
    alignItems: "center",
    fontWeight: 600,
    fontSize: 16,
    color: "var(--ink)",
  },
  windowHeaderStatus: {
    display: "flex",
    alignItems: "center",
    fontSize: 12.5,
    color: "var(--ink-faint)",
    height: 16,
    marginTop: 2,
  },
  windowEmpty: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    textAlign: "center",
    padding: 24,
    color: "var(--ink-faint)",
    fontSize: 14.5,
  },
  emptyTitle: { color: "var(--ink)", fontSize: 15.5, fontWeight: 600, margin: "6px 0 0" },
  emptySub: { color: "var(--ink-faint)", fontSize: 13.5, margin: 0 },
  messageList: {
    flex: 1,
    overflowY: "auto",
    padding: "18px 22px",
    display: "flex",
    flexDirection: "column",
    gap: 2,
    background: "color-mix(in srgb, var(--ink) 2%, var(--bg-surface))",
  },
  dateDivider: {
    alignSelf: "center",
    fontSize: 11.5,
    color: "var(--ink-faint)",
    background: SOFT,
    borderRadius: 999,
    padding: "3px 12px",
    margin: "12px 0 8px",
  },
  messageOuter: {
    display: "flex",
    maxWidth: "70%",
  },
  messageOuterMine: {
    alignSelf: "flex-end",
  },
  messageOuterOther: {
    alignSelf: "flex-start",
    alignItems: "flex-end",
    gap: 8,
  },
  messageAvatarSlot: {
    width: 28,
    flexShrink: 0,
  },
  messageAvatarImg: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    objectFit: "cover",
    display: "block",
  },
  messageAvatarPlaceholder: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    background: "color-mix(in srgb, var(--accent-color) 22%, var(--bg-surface))",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 600,
    color: "var(--accent-color)",
  },
  messageRow: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    gap: 3,
  },
  messageRowMine: {
    alignSelf: "flex-end",
    alignItems: "flex-end",
  },
  messageRowOther: {
    alignSelf: "flex-start",
    alignItems: "flex-start",
  },
  messageSender: {
    fontSize: 12,
    color: "var(--ink-faint)",
    margin: "6px 4px 0",
  },
  bubble: {
    padding: "8px 14px",
    borderRadius: 18,
    fontSize: 14.5,
    lineHeight: 1.5,
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
  },
  bubbleMine: {
    background: "var(--accent-color)",
    color: "#FFFDF8",
  },
  bubbleOther: {
    background: "var(--bg-surface)",
    color: "var(--ink)",
    border: `1px solid ${LINE}`,
  },
  messageTime: {
    fontSize: 11,
    color: "var(--ink-faint)",
    margin: "1px 4px 6px",
  },

  /* --- attachments --- */
  attachmentImageBtn: {
    display: "block",
    padding: 0,
    border: "none",
    background: "none",
    cursor: "zoom-in",
    borderRadius: 14,
  },
  /* --- stickers --- */
  stickerMsg: { padding: "2px 0" },
  stickerCatcher: { position: "fixed", inset: 0, zIndex: 40 },
  stickerTray: {
    position: "absolute",
    left: 16,
    bottom: "calc(100% - 4px)",
    zIndex: 41,
    width: 340,
    maxWidth: "calc(100% - 32px)",
    boxSizing: "border-box",
    background: "var(--bg-surface)",
    border: `1px solid ${LINE}`,
    borderRadius: 16,
    boxShadow: "0 10px 30px rgba(0,0,0,0.14)",
    padding: "12px 12px 10px",
  },
  stickerTrayTitle: { fontSize: 12.5, fontWeight: 600, color: "var(--ink-faint)", margin: "0 4px 8px" },
  stickerGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 6,
    maxHeight: 270,
    overflowY: "auto",
  },
  stickerCell: {
    aspectRatio: "1 / 1",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    border: "none",
    borderRadius: 12,
    background: SOFT,
    cursor: "pointer",
  },
  attachBtnOn: { background: ACCENT_SOFT, color: "var(--accent-color)" },

  /* --- image gallery --- */
  headerIconBtn: {
    position: "relative",
    flexShrink: 0,
    width: 38,
    height: 38,
    borderRadius: "50%",
    border: "none",
    background: "transparent",
    color: "var(--ink-soft)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: 0,
  },
  headerIconCount: {
    position: "absolute",
    top: 0,
    right: -2,
    minWidth: 16,
    height: 16,
    padding: "0 4px",
    boxSizing: "border-box",
    borderRadius: 8,
    background: "var(--accent-color)",
    color: "#FFFDF8",
    fontSize: 10,
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  galleryBackdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 1100,
    background: "rgba(12,14,20,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    boxSizing: "border-box",
  },
  galleryPanel: {
    width: "100%",
    maxWidth: 760,
    maxHeight: "86vh",
    display: "flex",
    flexDirection: "column",
    background: "var(--bg-surface)",
    color: "var(--ink)",
    border: `1px solid ${LINE}`,
    borderRadius: 18,
    overflow: "hidden",
    boxShadow: "0 18px 50px rgba(0,0,0,0.25)",
  },
  galleryHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "16px 20px",
    borderBottom: `1px solid ${LINE}`,
  },
  galleryTitle: { fontSize: 16.5, fontWeight: 600 },
  gallerySub: { fontSize: 12.5, color: "var(--ink-faint)", marginTop: 2 },
  galleryClose: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    border: "none",
    background: SOFT,
    color: "var(--ink)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: 0,
    flexShrink: 0,
  },
  galleryBody: { overflowY: "auto", padding: "6px 20px 20px" },
  galleryDay: { fontSize: 12.5, color: "var(--ink-faint)", fontWeight: 600, margin: "14px 0 8px" },
  galleryGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))", gap: 6 },
  galleryThumbBtn: {
    padding: 0,
    border: "none",
    background: SOFT,
    borderRadius: 10,
    overflow: "hidden",
    cursor: "zoom-in",
    aspectRatio: "1 / 1",
    display: "block",
  },
  galleryThumb: { width: "100%", height: "100%", objectFit: "cover", display: "block" },

  /* --- image viewer --- */
  viewerBackdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 1300,
    background: "rgba(12,14,20,0.88)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "72px 64px 32px",
    boxSizing: "border-box",
  },
  viewerBar: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "14px 20px",
    color: "#fff",
  },
  viewerTitle: { fontSize: 15, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  viewerSub: { fontSize: 12.5, opacity: 0.7, marginTop: 2 },
  viewerBtn: {
    width: 38,
    height: 38,
    borderRadius: "50%",
    border: "none",
    background: "rgba(255,255,255,0.14)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: 0,
    textDecoration: "none",
  },
  viewerNav: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    width: 44,
    height: 44,
    borderRadius: "50%",
    border: "none",
    background: "rgba(255,255,255,0.14)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: 0,
  },
  viewerImg: {
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
    borderRadius: 8,
    boxShadow: "0 10px 40px rgba(0,0,0,0.5)",
  },
  attachmentImage: {
    display: "block",
    maxWidth: 240,
    maxHeight: 240,
    borderRadius: 14,
    border: `1px solid ${LINE}`,
    objectFit: "cover",
  },
  attachmentFileChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 12px",
    borderRadius: 12,
    border: `1px solid ${LINE}`,
    background: "var(--bg-surface)",
    color: "var(--ink)",
    textDecoration: "none",
    fontSize: 13,
    maxWidth: 220,
  },
  attachmentFileIcon: {
    display: "flex",
    flexShrink: 0,
    color: "var(--ink-soft)",
  },
  attachmentFileName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  /* --- composer --- */
  composerWrap: {
    position: "relative",
    borderTop: `1px solid ${LINE}`,
    flexShrink: 0,
    padding: "12px 16px 14px",
    background: "var(--bg-surface)",
  },
  composerPreview: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "0 4px 10px",
  },
  composerPreviewImage: {
    width: 44,
    height: 44,
    borderRadius: 10,
    border: `1px solid ${LINE}`,
    objectFit: "cover",
  },
  composerPreviewMeta: {
    fontSize: 12,
    color: "var(--ink-faint)",
    flex: 1,
  },
  composerPreviewRemove: {
    background: "none",
    border: "none",
    cursor: "pointer",
    display: "flex",
    color: "var(--ink-faint)",
    padding: 4,
  },
  composer: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 5px 5px 6px",
    background: SOFT,
    border: "1px solid transparent",
    borderRadius: 999,
  },
  attachBtn: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    border: "none",
    background: "transparent",
    color: "var(--ink-soft)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
    padding: 0,
  },
  composerInput: {
    flex: 1,
    minWidth: 0,
    fontFamily: "inherit",
    fontSize: 14.5,
    color: "var(--ink)",
    background: "transparent",
    border: "none",
    outline: "none",
    padding: "8px 4px",
  },
  sendBtn: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    border: "none",
    background: "var(--accent-color)",
    color: "#FFFDF8",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
    padding: 0,
  },
  sendBtnOff: {
    opacity: 0.35,
    cursor: "default",
  },
};

export default styles;
